import { useState } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { Badge, Button, Card, CardSection, Field, Input, Select, Toggle, useToast } from "@/components/ui";
import type { Organization } from "@/lib/types/api";

/**
 * Merchant payment configuration.
 *
 * Credentials are write-only: they're encrypted at rest and never come back
 * from the API. The UI shows which keys are set, never their values — so an
 * operator can confirm the account is configured without the secrets being
 * readable by anyone who gets at a browser session.
 */

const PROVIDERS = [
  { value: "null", label: "None — snapshots are free" },
  { value: "telebirr", label: "telebirr (Ethio Telecom)" },
  { value: "arifpay", label: "ArifPay · Telebirr USSD" },
];

const TELEBIRR_FIELDS: Array<{ key: string; label: string; hint?: string; multiline?: boolean }> = [
  { key: "app_id", label: "Merchant App ID", hint: "From the telebirr merchant credential panel." },
  { key: "fabric_app_id", label: "Fabric App ID", hint: "Required by the H5 payment integration." },
  { key: "short_code", label: "Short Code" },
  { key: "app_secret", label: "App Secret", hint: "The App Secret issued with the merchant application." },
  { key: "private_key", label: "Merchant Private Key", hint: "Used to sign requests. Keep this secret.", multiline: true },
  { key: "receive_name", label: "Display name", hint: "What the payer sees in telebirr." },
  { key: "public_key", label: "telebirr Public Key", hint: "telebirr's public key. Used to encrypt the request payload.", multiline: true },
  { key: "base_url", label: "API base URL", hint: "Leave blank for the production host." },
];

const ARIFPAY_FIELDS: Array<{ key: string; label: string; hint?: string; type?: string }> = [
  { key: "api_key", label: "ArifPay API key", hint: "Sent server-to-server as x-arifpay-key.", type: "password" },
  { key: "secret_key", label: "ArifPay secret key", hint: "Stored encrypted for future webhook verification; not sent in the direct-transfer request.", type: "password" },
  { key: "generic_email", label: "Generic checkout email", hint: "Required by ArifPay; guests are not asked for an email." },
  { key: "environment", label: "Environment", hint: "Sandbox uses the ArifPay sandbox gateway and customer-paid simulated fee." },
  { key: "base_url", label: "Custom API base URL", hint: "Optional. Leave blank to use the selected environment default." },
];

export function PaymentsSettings({ organization, onSaved }: {
  organization: Organization;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(organization.payments_enabled ?? false);
  const [provider, setProvider] = useState(organization.payment_provider ?? "null");
  const [currency, setCurrency] = useState(organization.currency ?? "ETB");
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [h5Token, setH5Token] = useState("");
  const [h5Order, setH5Order] = useState<{ merchant_order_id?: string; prepay_id?: string; checkout_url?: string }>({});
  const [h5Title, setH5Title] = useState("JebaAI test snapshot");
  const [h5Amount, setH5Amount] = useState("1.00");
  const [h5Busy, setH5Busy] = useState("");
  const [h5Message, setH5Message] = useState("");

  const configured = organization.configured_payment_keys ?? [];
  const dirty = Object.values(credentials).some((value) => value.trim().length > 0);

  async function save() {
    setSaving(true);
    try {
      await adminApi.organizations.update(organization.id, {
        payments_enabled: enabled,
        payment_provider: provider,
        currency,
        ...(dirty ? { payment_credentials: credentials } : {}),
      });
      setCredentials({});
      toast.success("Payment settings saved");
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function runH5(stage: string, action: () => Promise<Record<string, unknown>>) {
    setH5Busy(stage); setH5Message("");
    try {
      const result = await action();
      if (stage === "token" && typeof result.token === "string") setH5Token(result.token);
      if (stage === "order") setH5Order({ merchant_order_id: result.merchant_order_id as string, prepay_id: result.prepay_id as string, checkout_url: result.checkout_url as string });
      setH5Message(`${stage} completed. Response received from Telebirr.`);
    } catch (error) { setH5Message(error instanceof Error ? error.message : `${stage} failed`); }
    finally { setH5Busy(""); }
  }

  return (
    <CardSection
      eyebrow="Payments"
      title="Merchant account"
      description="Guests pay for snapshots on their own phone. Money settles into your merchant account — it never passes through us."
    >
      <Card className="flex flex-col gap-5 p-5">
        <Toggle
          checked={enabled}
          onChange={setEnabled}
          label="Charge guests for snapshots"
          hint="With this off, snapshots still work — they're just free, and no order is created."
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Provider">
            <Select value={provider} onChange={(e) => setProvider(e.target.value)}>
              {PROVIDERS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Select>
          </Field>

          <Field label="Currency">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {["ETB", "USD", "EUR", "GBP", "KES", "NGN"].map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </Select>
          </Field>
        </div>

        {provider === "telebirr" && (
          <fieldset className="flex flex-col gap-5 rounded-xl border border-ink-600 bg-ink-800/60 p-4">
            <legend className="label-eyebrow px-1">telebirr credentials</legend>

            {configured.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-haze-400">Already set:</span>
                {configured.map((key) => <Badge key={key} tone="ok">{key}</Badge>)}
              </div>
            )}

            {TELEBIRR_FIELDS.map((field) => (
              <Field
                key={field.key}
                label={field.label}
                hint={configured.includes(field.key) ? `${field.hint ?? ""} Leave blank to keep the saved value.`.trim() : field.hint}
              >
                <Input
                  type={field.key.includes("key") && !field.multiline ? "password" : "text"}
                  spellCheck={false}
                  className={field.multiline ? "font-mono text-xs" : undefined}
                  placeholder={configured.includes(field.key) ? "••••••••" : ""}
                  value={credentials[field.key] ?? ""}
                  onChange={(e) => setCredentials((current) => ({ ...current, [field.key]: e.target.value }))}
                />
              </Field>
            ))}

            <p className="rounded-lg bg-ink-900 px-3 py-2 text-xs leading-relaxed text-haze-400">
              Credentials are encrypted at rest and never returned by the API — this form can set
              them but not read them back. Field names and the API host differ between telebirr
              integration versions, so check these against your own merchant onboarding pack and
              run a sandbox payment before taking real money.
            </p>
          </fieldset>
        )}

        {provider === "arifpay" && (
          <fieldset className="flex flex-col gap-5 rounded-xl border border-ink-600 bg-ink-800/60 p-4">
            <legend className="label-eyebrow px-1">ArifPay · Telebirr USSD</legend>
            {configured.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-haze-400">Already set:</span>
                {configured.filter((key) => key.startsWith("arifpay_")).map((key) => <Badge key={key} tone="ok">{key.replace("arifpay_", "")}</Badge>)}
              </div>
            )}
            {ARIFPAY_FIELDS.map((field) => (
              <Field key={field.key} label={field.label} hint={configured.includes(`arifpay_${field.key}`) ? `${field.hint ?? ""} Leave blank to keep the saved value.`.trim() : field.hint}>
                {field.key === "environment" ? (
                  <Select value={credentials.arifpay_environment ?? "sandbox"} onChange={(e) => setCredentials((current) => ({ ...current, arifpay_environment: e.target.value }))}>
                    <option value="sandbox">Sandbox (default)</option>
                    <option value="production">Production</option>
                  </Select>
                ) : (
                  <Input
                    type={field.type ?? "text"}
                    spellCheck={false}
                    placeholder={configured.includes(`arifpay_${field.key}`) ? "••••••••" : ""}
                    value={credentials[`arifpay_${field.key}`] ?? ""}
                    onChange={(e) => setCredentials((current) => ({ ...current, [`arifpay_${field.key}`]: e.target.value }))}
                  />
                )}
              </Field>
            ))}
            <p className="rounded-lg bg-ink-900 px-3 py-2 text-xs leading-relaxed text-haze-400">
              Credentials are encrypted at rest and write-only. This integration uses phone push and waits for the ArifPay callback. Callback signature/status verification is intentionally not enabled yet; add it here when ArifPay provides the production verification contract.
            </p>
          </fieldset>
        )}

        {provider === "telebirr" && (
          <Card className="flex flex-col gap-4 border-warn/40 bg-warn/5 p-5">
            <div>
              <h3 className="font-semibold text-ink-100">Telebirr H5 test bed</h3>
              <p className="mt-1 text-xs leading-relaxed text-haze-400">Runs the sandbox sequence from the server: Fabric token, create order, checkout URL, query, and refund. It does not create a Jeba guest order.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Test title"><Input value={h5Title} onChange={(e) => setH5Title(e.target.value)} /></Field>
              <Field label="Amount (ETB)"><Input value={h5Amount} onChange={(e) => setH5Amount(e.target.value)} /></Field>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button loading={h5Busy === "token"} onClick={() => void runH5("token", () => adminApi.organizations.telebirrH5.token(organization.id))}>1. Apply Fabric token</Button>
              <Button loading={h5Busy === "order"} disabled={!h5Token} onClick={() => void runH5("order", () => adminApi.organizations.telebirrH5.order(organization.id, { token: h5Token, title: h5Title, amount: h5Amount }))}>2. Create test order</Button>
              <Button loading={h5Busy === "query"} disabled={!h5Token || !h5Order.merchant_order_id} onClick={() => void runH5("query", () => adminApi.organizations.telebirrH5.query(organization.id, { token: h5Token, merchant_order_id: h5Order.merchant_order_id! }))}>3. Query order</Button>
              <Button loading={h5Busy === "refund"} disabled={!h5Token || !h5Order.merchant_order_id} onClick={() => void runH5("refund", () => adminApi.organizations.telebirrH5.refund(organization.id, { token: h5Token, merchant_order_id: h5Order.merchant_order_id!, amount: h5Amount, reason: "Admin H5 test" }))}>4. Refund test order</Button>
            </div>
            {h5Order.checkout_url && <a className="break-all text-sm text-lime underline" href={h5Order.checkout_url} target="_blank" rel="noreferrer">Open generated checkout URL</a>}
            {h5Message && <p className="text-xs text-haze-300">{h5Message}</p>}
            <p className="text-xs text-haze-500">The Fabric token is kept only in this browser tab and is never displayed. Use sandbox credentials and verify the endpoint before enabling live charging.</p>
          </Card>
        )}

        {provider === "null" && enabled && (
          <p className="rounded-lg border border-warn/40 bg-warn/5 px-3 py-2 text-xs leading-relaxed text-warn">
            Payments are on but no provider is selected, so orders settle immediately without anyone
            being charged. That's the right setting for testing the flow, and the wrong one for a
            live event.
          </p>
        )}

        <div className="flex justify-end">
          <Button variant="primary" loading={saving} onClick={save}>Save payment settings</Button>
        </div>
      </Card>
    </CardSection>
  );
}
